import toast from 'react-hot-toast';
import { serviceFactory } from './api/ApiServiceFactory';
import { AuthService } from './auth/AuthService';
import { TokenManager } from './auth/TokenManager';
import { UserService } from './user/UserService';
import { LanguageService } from './language/LanguageService';
import { GenreService } from './genre/GenreService';
import { MovieService } from './movie/MovieService';
import { EpisodeService } from './episode/EpisodeService';
import { DramaService } from './drama/DramaService';
import { CastService } from './cast/CastService';
import { MusicVideoService } from './music-video/MusicVideoService';
import { DocumentaryService } from './documentary/DocumentaryService';
import { SubscriptionService } from './subscription/SubscriptionService';
import { BannerService } from './banner/BannerService';
import { TrailerService } from './trailer/TrailerService';
import { SplashScreenService } from './splash-screen/SplashScreenService';
import { ContentService } from './content/ContentService';
import { AppUpdateService } from './app-update/AppUpdateService';
import { PaymentMethodService } from './payment-method/PaymentMethodService';
import { DashboardService } from './dashboard/DashboardService';
import { AlbumService } from './album/AlbumService';
import { SongService } from './song/SongService';
import { ArtistService } from './artist/ArtistService';

/**
 * Service Registry
 * 
 * Central registration point for all application services.
 * This file registers all services with the service factory
 * for dependency injection throughout the application.
 */
export class ServiceRegistry {
  private static initialized = false;

  /**
   * Initialize and register all application services
   */
  public static initialize(): void {
    if (ServiceRegistry.initialized) {
      return;
    }

    // Register Authentication services
    serviceFactory.register(
      'AuthService',
      () => new AuthService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    serviceFactory.register(
      'TokenManager',
      () => TokenManager.getInstance(),
      { singleton: true }
    );

    // Register User services
    serviceFactory.register(
      'UserService',
      () => new UserService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Language services
    serviceFactory.register(
      'LanguageService',
      () => new LanguageService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Genre services
    serviceFactory.register(
      'GenreService',
      () => new GenreService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Movie services
    serviceFactory.register(
      'MovieService',
      () => new MovieService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Cast services
    serviceFactory.register(
      'CastService',
      () => new CastService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Episode services
    serviceFactory.register(
      'EpisodeService',
      () => new EpisodeService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Drama / Series services
    serviceFactory.register(
      'DramaService',
      () => new DramaService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Music Video services
    serviceFactory.register(
      'MusicVideoService',
      () => new MusicVideoService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Documentary services
    serviceFactory.register(
      'DocumentaryService',
      () => new DocumentaryService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Subscription services
    serviceFactory.register(
      'SubscriptionService',
      () => new SubscriptionService(serviceFactory.resolve('HttpClient')),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Banner services
    serviceFactory.register(
      'BannerService',
      () => new BannerService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Trailer services
    serviceFactory.register(
      'TrailerService',
      () => new TrailerService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Splash Screen services
    serviceFactory.register(
      'SplashScreenService',
      () => new SplashScreenService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Content services
    serviceFactory.register(
      'ContentService',
      () => new ContentService(serviceFactory.resolve('HttpClient')),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register App Update services
    serviceFactory.register(
      'AppUpdateService',
      () => new AppUpdateService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Payment Method services
    serviceFactory.register(
      'PaymentMethodService',
      () => new PaymentMethodService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    serviceFactory.register(
      'DashboardService',
      () => new DashboardService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    serviceFactory.register(
      'AlbumService',
      () => new AlbumService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    serviceFactory.register(
      'SongService',
      () => new SongService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    serviceFactory.register(
      'ArtistService',
      () => new ArtistService(),
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Order services (to be implemented)
    serviceFactory.register(
      'OrderService',
      () => {
        // Placeholder for OrderService
        return {};
      },
      { singleton: true, dependencies: ['HttpClient'] }
    );

    // Register Payment services (to be implemented)
    serviceFactory.register(
      'PaymentService',
      () => {
        // Placeholder for PaymentService
        return {};
      },
      { singleton: true, dependencies: ['HttpClient'] }
    );

    ServiceRegistry.initialized = true;
  }

  /**
   * Get service instance by name
   */
  public static getService<T>(serviceName: string): T {
    if (!ServiceRegistry.initialized) {
      ServiceRegistry.initialize();
    }

    try {
      const service = serviceFactory.resolve<T>(serviceName);
      return service;
    } catch (error: any) {
      toast.error(`Failed to load ${serviceName}`, {
        icon: '⚠️',
      });
      throw error;
    }
  }

  /**
   * Check if service is registered
   */
  public static hasService(serviceName: string): boolean {
    return serviceFactory.isRegistered(serviceName);
  }

  /**
   * Get all registered services health status
   */
  public static getHealthStatus() {
    return serviceFactory.getAllServicesHealth();
  }

  /**
   * Enable mock mode for testing
   */
  public static enableMockMode(): void {
    serviceFactory.enableMockMode();
  }

  /**
   * Disable mock mode
   */
  public static disableMockMode(): void {
    serviceFactory.disableMockMode();
  }

  /**
   * Register mock services for testing
   */
  public static registerMockServices(mocks: Record<string, () => any>): void {
    Object.entries(mocks).forEach(([serviceName, mockFactory]) => {
      serviceFactory.registerMock(serviceName, mockFactory);
    });
  }

  /**
   * Reset all services (useful for testing)
   */
  public static reset(): void {
    serviceFactory.reset();
    ServiceRegistry.initialized = false;
  }
}

// Convenience functions for commonly used services
export const getAuthService = (): AuthService => 
  ServiceRegistry.getService<AuthService>('AuthService');

export const getTokenManager = (): TokenManager => 
  ServiceRegistry.getService<TokenManager>('TokenManager');

export const getUserService = (): UserService => 
  ServiceRegistry.getService<UserService>('UserService');

export const getLanguageService = (): LanguageService => 
  ServiceRegistry.getService<LanguageService>('LanguageService');

export const getGenreService = (): GenreService => 
  ServiceRegistry.getService<GenreService>('GenreService');

export const getMovieService = (): MovieService => 
  ServiceRegistry.getService<MovieService>('MovieService');

export const getEpisodeService = (): EpisodeService =>
  ServiceRegistry.getService<EpisodeService>('EpisodeService');

export const getDramaService = (): DramaService =>
  ServiceRegistry.getService<DramaService>('DramaService');

export const getCastService = (): CastService =>
  ServiceRegistry.getService<CastService>('CastService');

export const getMusicVideoService = (): MusicVideoService =>
  ServiceRegistry.getService<MusicVideoService>('MusicVideoService');

export const getDocumentaryService = (): DocumentaryService =>
  ServiceRegistry.getService<DocumentaryService>('DocumentaryService');

export const getSubscriptionService = (): SubscriptionService =>
  ServiceRegistry.getService<SubscriptionService>('SubscriptionService');

export const getBannerService = (): BannerService =>
  ServiceRegistry.getService<BannerService>('BannerService');

export const getTrailerService = (): TrailerService =>
  ServiceRegistry.getService<TrailerService>('TrailerService');

export const getSplashScreenService = (): SplashScreenService =>
  ServiceRegistry.getService<SplashScreenService>('SplashScreenService');

export const getContentService = (): ContentService =>
  ServiceRegistry.getService<ContentService>('ContentService');

export const getAppUpdateService = (): AppUpdateService =>
  ServiceRegistry.getService<AppUpdateService>('AppUpdateService');

export const getPaymentMethodService = (): PaymentMethodService =>
  ServiceRegistry.getService<PaymentMethodService>('PaymentMethodService');

export const getDashboardService = (): DashboardService =>
  ServiceRegistry.getService<DashboardService>('DashboardService');

export const getAlbumService = (): AlbumService =>
  ServiceRegistry.getService<AlbumService>('AlbumService');

export const getSongService = (): SongService =>
  ServiceRegistry.getService<SongService>('SongService');

export const getArtistService = (): ArtistService =>
  ServiceRegistry.getService<ArtistService>('ArtistService');

// Initialize services when module is imported
ServiceRegistry.initialize();
